import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Eye, FileText, Search, Loader2, CalendarIcon, Trash2 } from 'lucide-react';
import { ticketService } from '../../services/ticketService';
import { clientService } from '../../services/clientService';
import { userService } from '../../services/userService';
import { useToast } from '../../hooks/use-toast';

// Note: Calendar and Popover imports - check if these exist in your project
// If not, we'll use native date inputs as fallback

const Chamados = () => {
  const { toast } = useToast();
  const [tickets, setTickets] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [newInteraction, setNewInteraction] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [reportData, setReportData] = useState(null);
  const [reportDates, setReportDates] = useState({
    start_date: '',
    end_date: '',
    client_id: 'all'
  });
  const [newTicketData, setNewTicketData] = useState({
    title: '',
    description: '',
    priority: 'media',
    client_id: ''
  });
  
  // Estados para o novo modal de relatórios
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    start_date: null,
    end_date: null,
    status: [],
    client_id: null,
    responsible_id: null,
    created_by: null
  });
  const [reportPreview, setReportPreview] = useState({ count: 0, loading: false });
  const [generatingReport, setGeneratingReport] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Regular users can't access users endpoint
      if (currentUser.role === 'admin' || currentUser.role === 'tecnico') {
        const [ticketsData, clientsData, usersData] = await Promise.all([
          ticketService.getAll(),
          clientService.getAll(),
          userService.getAll()
        ]);
        setTickets(ticketsData);
        setClients(clientsData);
        setUsers(usersData);
      } else {
        // Regular users only fetch tickets and clients
        const [ticketsData, clientsData] = await Promise.all([
          ticketService.getAll(),
          clientService.getAll()
        ]);
        setTickets(ticketsData);
        setClients(clientsData);
        setUsers([]); // Empty users list for regular users
      }
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      toast({
        title: 'Erro ao carregar chamados',
        description: 'Não foi possível carregar os chamados.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };
  
  const getClientName = (clientId) => {
    const client = clients.find(c => c._id === clientId);
    return client ? client.name : 'Cliente desconhecido';
  };

  const getUserName = (userId) => {
    if (!userId) return 'Não atribuído';
    const user = users.find(u => u._id === userId);
    return user ? user.name : 'Desconhecido';
  };

  const handleViewDetails = async (ticket) => {
    setSelectedTicket(ticket);
    setDetailsOpen(true);
    try {
      const interactionsData = await ticketService.getInteractions(ticket._id);
      setInteractions(interactionsData);
    } catch (error) {
      console.error('Erro ao buscar interações:', error);
    }
  };

  const handleAddInteraction = async () => {
    if (!newInteraction.trim()) return;
    
    try {
      await ticketService.addInteraction(selectedTicket._id, {
        content: newInteraction,
        type: 'comment'
      });
      const interactionsData = await ticketService.getInteractions(selectedTicket._id);
      setInteractions(interactionsData);
      setNewInteraction('');
      toast({
        title: 'Interação adicionada',
        description: 'Seu comentário foi adicionado com sucesso.',
      });
    } catch (error) {
      console.error('Erro ao adicionar interação:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível adicionar a interação.',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateTicket = async (field, value) => {
    try {
      await ticketService.update(selectedTicket._id, { [field]: value });
      
      // Update local state
      setSelectedTicket(prev => ({ ...prev, [field]: value }));
      
      // Refresh tickets list
      fetchData();
      
      toast({
        title: 'Chamado atualizado',
        description: 'As alterações foram salvas com sucesso.',
      });
    } catch (error) {
      console.error('Erro ao atualizar chamado:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível atualizar o chamado.',
        variant: 'destructive',
      });
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      // Get current user
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      
      // If user is not admin/tecnico, use their client_id
      const ticketData = { ...newTicketData };
      if (currentUser.role === 'user' && currentUser.client_id) {
        ticketData.client_id = currentUser.client_id;
      }
      
      await ticketService.create(ticketData);
      toast({
        title: 'Chamado criado',
        description: 'O chamado foi criado com sucesso.',
      });
      setCreateDialogOpen(false);
      setNewTicketData({
        title: '',
        description: '',
        priority: 'media',
        client_id: ''
      });
      fetchData();
    } catch (error) {
      console.error('Erro ao criar chamado:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || 'Não foi possível criar o chamado.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteTicket = async (ticketId, ticketNumber) => {
    // Confirmar exclusão
    const confirmDelete = window.confirm(
      `Tem certeza que deseja excluir o chamado #${String(ticketNumber).padStart(4, '0')}?\n\nEsta ação não pode ser desfeita.`
    );
    
    if (!confirmDelete) return;

    try {
      await ticketService.delete(ticketId);
      toast({
        title: 'Chamado excluído',
        description: 'O chamado foi excluído com sucesso.',
      });
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir chamado:', error);
      toast({
        title: 'Erro ao excluir',
        description: error.response?.data?.detail || 'Não foi possível excluir o chamado.',
        variant: 'destructive',
      });
    }
  };


  const handleGenerateReport = () => {
    const filtered = tickets.filter(ticket => {
      const ticketDate = new Date(ticket.created_at);
      const startDate = reportDates.start_date ? new Date(reportDates.start_date) : null;
      const endDate = reportDates.end_date ? new Date(reportDates.end_date) : null;
      
      if (startDate && ticketDate < startDate) return false;
      if (endDate && ticketDate > endDate) return false;
      if (reportDates.client_id !== 'all' && ticket.client_id !== reportDates.client_id) return false;
      return true;
    });

    // Group by client
    const byClient = {};
    filtered.forEach(ticket => {
      const clientName = getClientName(ticket.client_id);
      if (!byClient[clientName]) {
        byClient[clientName] = { total: 0, novo: 0, em_andamento: 0, concluido: 0 };
      }
      byClient[clientName].total++;
      byClient[clientName][ticket.status]++;
    });

    setReportData({
      total: filtered.length,
      byStatus: {
        novo: filtered.filter(t => t.status === 'novo').length,
        em_andamento: filtered.filter(t => t.status === 'em_andamento').length,
        concluido: filtered.filter(t => t.status === 'concluido').length,
        cancelado: filtered.filter(t => t.status === 'cancelado').length
      },
      byClient
    });
  };

  // Novas funções para o modal de relatórios com backend
  const updateReportPreview = async (filters) => {
    setReportPreview({ ...reportPreview, loading: true });
    try {
      const preview = await ticketService.previewReport(filters);
      setReportPreview({ count: preview.count, loading: false });
    } catch (error) {
      setReportPreview({ count: 0, loading: false });
    }
  };

  const handleGenerateReportPDF = async () => {
    setGeneratingReport(true);
    try {
      await ticketService.generateReport(reportFilters);
      toast({
        title: "Sucesso!",
        description: "Relatório gerado e baixado com sucesso.",
      });
      setShowReportModal(false);
      // Limpar filtros
      setReportFilters({
        start_date: null,
        end_date: null,
        status: [],
        client_id: null,
        responsible_id: null,
        created_by: null
      });
    } catch (error) {
      toast({
        title: "Erro ao gerar relatório",
        description: "Não foi possível gerar o relatório. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setGeneratingReport(false);
    }
  };

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdminOrTech = currentUser.role === 'admin' || currentUser.role === 'tecnico';

  // Função de pesquisa por termo - busca em TODOS os tickets quando há searchTerm
  const searchTickets = (ticketList) => {
    if (!searchTerm) return ticketList;
    const search = searchTerm.toLowerCase().replace('#', ''); // Remove # se existir
    
    // Para admin/técnico: buscar em TODOS os tickets, não apenas na lista fornecida
    const listToSearch = isAdminOrTech ? tickets : ticketList;
    
    return listToSearch.filter(ticket => {
      const ticketNumber = String(ticket.ticket_number).padStart(4, '0');
      return (
        ticketNumber.includes(search) ||
        String(ticket.ticket_number).includes(search) ||
        ticket._id.toLowerCase().includes(search) ||
        ticket.title.toLowerCase().includes(search) ||
        getClientName(ticket.client_id).toLowerCase().includes(search) ||
        getUserName(ticket.created_by).toLowerCase().includes(search) ||
        getUserName(ticket.responsible_id).toLowerCase().includes(search)
      );
    });
  };

  // Tickets da Fila de Espera (novos e sem responsável)
  const queueTickets = searchTerm 
    ? searchTickets(tickets) // Pesquisa global quando há termo de busca
    : tickets.filter(ticket => 
        ticket.status === 'novo' && (!ticket.responsible_id || ticket.responsible_id === '')
      );

  // Meus Chamados (atribuídos ao usuário logado e NÃO finalizados)
  const myTickets = searchTerm
    ? searchTickets(tickets) // Pesquisa global quando há termo de busca
    : tickets.filter(ticket => 
        ticket.responsible_id === currentUser._id && 
        ticket.status !== 'concluido' && 
        ticket.status !== 'cancelado'
      );

  // Chamados Finalizados (concluídos ou cancelados, independente do responsável)
  const finishedTickets = searchTerm
    ? searchTickets(tickets) // Pesquisa global quando há termo de busca
    : tickets.filter(ticket => 
        ticket.status === 'concluido' || ticket.status === 'cancelado'
      );

  // Para usuários comuns, mantém a lógica original
  const filteredTickets = tickets.filter(ticket => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase().replace('#', ''); // Remove # se existir
    const ticketNumber = String(ticket.ticket_number).padStart(4, '0');
    return (
      ticketNumber.includes(search) ||
      String(ticket.ticket_number).includes(search) ||
      ticket._id.toLowerCase().includes(search) ||
      ticket.title.toLowerCase().includes(search) ||
      getClientName(ticket.client_id).toLowerCase().includes(search) ||
      getUserName(ticket.created_by).toLowerCase().includes(search) ||
      getUserName(ticket.responsible_id).toLowerCase().includes(search)
    );
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'novo':
        return 'bg-yellow-100 text-yellow-800';
      case 'em_andamento':
        return 'bg-blue-100 text-blue-800';
      case 'concluido':
        return 'bg-green-100 text-green-800';
      case 'cancelado':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'alta':
        return 'bg-red-100 text-red-800';
      case 'media':
        return 'bg-yellow-100 text-yellow-800';
      case 'baixa':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Chamados</h1>
          <p className="text-gray-600 mt-1">Gerencie todos os chamados do sistema</p>
        </div>
        <div className="flex gap-2">
          {(() => {
            const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
            return currentUser.role === 'admin' || currentUser.role === 'tecnico' ? (
              <Button
                onClick={() => setShowReportModal(true)}
                variant="outline"
                className="gap-2"
              >
                <FileText className="w-4 h-4" />
                Relatórios
              </Button>
            ) : null;
          })()}
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="bg-[#4A2B7F] hover:bg-[#6B46C1]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Chamado
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Chamados</CardTitle>
          <CardDescription>Visualize e gerencie todos os chamados</CardDescription>
          
          {/* Search Filter */}
          <div className="mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por ID, título, cliente ou criador..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : isAdminOrTech ? (
            // Visualização com abas para Técnicos e Administradores
            <Tabs defaultValue="queue" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="queue">
                  Fila de Espera ({queueTickets.length})
                </TabsTrigger>
                <TabsTrigger value="mine">
                  Meus Chamados ({myTickets.length})
                </TabsTrigger>
                <TabsTrigger value="finished">
                  Finalizados ({finishedTickets.length})
                </TabsTrigger>
              </TabsList>

              {/* Aba Fila de Espera */}
              <TabsContent value="queue">
                <div className="overflow-x-auto">
                  {queueTickets.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      Nenhum chamado na fila de espera
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">ID</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Título</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Cliente/Empresa</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Criado por</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Responsável</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Prioridade</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Data</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {queueTickets.map((ticket) => (
                          <tr key={ticket._id} className="border-b hover:bg-gray-50 transition-colors">
                            <td className="py-3 px-4">
                              <div className="text-sm font-semibold text-gray-700">
                                #{String(ticket.ticket_number).padStart(4, '0')}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div>
                                <div className="font-medium text-gray-900">{ticket.title}</div>
                                <div className="text-sm text-gray-600">{ticket.description}</div>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-gray-700">{getClientName(ticket.client_id)}</td>
                            <td className="py-3 px-4 text-gray-700">{getUserName(ticket.created_by)}</td>
                            <td className="py-3 px-4 text-gray-700">{getUserName(ticket.responsible_id)}</td>
                            <td className="py-3 px-4">
                              <Badge className={getStatusColor(ticket.status)}>
                                {ticket.status === 'novo' ? 'Novo' : ticket.status === 'em_andamento' ? 'Em Andamento' : ticket.status === 'concluido' ? 'Concluído' : 'Cancelado'}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              <Badge className={getPriorityColor(ticket.priority)}>
                                {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-gray-700">
                              {new Date(ticket.created_at).toLocaleDateString('pt-BR')}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleViewDetails(ticket)}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Ver
                                </Button>
                                {isAdminOrTech && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDeleteTicket(ticket._id, ticket.ticket_number)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </TabsContent>

              {/* Aba Meus Chamados */}
              <TabsContent value="mine">
                <div className="overflow-x-auto">
                  {myTickets.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      Nenhum chamado atribuído a você
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">ID</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Título</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Cliente/Empresa</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Criado por</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Prioridade</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Data</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {myTickets.map((ticket) => (
                          <tr key={ticket._id} className="border-b hover:bg-gray-50 transition-colors">
                            <td className="py-3 px-4">
                              <div className="text-sm font-semibold text-gray-700">
                                #{String(ticket.ticket_number).padStart(4, '0')}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div>
                                <div className="font-medium text-gray-900">{ticket.title}</div>
                                <div className="text-sm text-gray-600">{ticket.description}</div>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-gray-700">{getClientName(ticket.client_id)}</td>
                            <td className="py-3 px-4 text-gray-700">{getUserName(ticket.created_by)}</td>
                            <td className="py-3 px-4">
                              <Badge className={getStatusColor(ticket.status)}>
                                {ticket.status === 'novo' ? 'Novo' : ticket.status === 'em_andamento' ? 'Em Andamento' : ticket.status === 'concluido' ? 'Concluído' : 'Cancelado'}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              <Badge className={getPriorityColor(ticket.priority)}>
                                {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-gray-700">
                              {new Date(ticket.created_at).toLocaleDateString('pt-BR')}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleViewDetails(ticket)}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Ver
                                </Button>
                                {isAdminOrTech && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDeleteTicket(ticket._id, ticket.ticket_number)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </TabsContent>

              {/* Aba Chamados Finalizados */}
              <TabsContent value="finished">
                <div className="overflow-x-auto">
                  {finishedTickets.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      Nenhum chamado finalizado
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">ID</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Título</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Cliente/Empresa</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Criado por</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Responsável</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Prioridade</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Data</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {finishedTickets.map((ticket) => (
                          <tr key={ticket._id} className="border-b hover:bg-gray-50 transition-colors">
                            <td className="py-3 px-4">
                              <div className="text-sm font-semibold text-gray-700">
                                #{String(ticket.ticket_number).padStart(4, '0')}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div>
                                <div className="font-medium text-gray-900">{ticket.title}</div>
                                <div className="text-sm text-gray-600">{ticket.description}</div>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-gray-700">{getClientName(ticket.client_id)}</td>
                            <td className="py-3 px-4 text-gray-700">{getUserName(ticket.created_by)}</td>
                            <td className="py-3 px-4 text-gray-700">{getUserName(ticket.responsible_id)}</td>
                            <td className="py-3 px-4">
                              <Badge className={getStatusColor(ticket.status)}>
                                {ticket.status === 'novo' ? 'Novo' : ticket.status === 'em_andamento' ? 'Em Andamento' : ticket.status === 'concluido' ? 'Concluído' : 'Cancelado'}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              <Badge className={getPriorityColor(ticket.priority)}>
                                {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-gray-700">
                              {new Date(ticket.created_at).toLocaleDateString('pt-BR')}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleViewDetails(ticket)}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Ver
                                </Button>
                                {isAdminOrTech && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDeleteTicket(ticket._id, ticket.ticket_number)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            // Visualização para usuários comuns (sem abas)
            <div className="overflow-x-auto">
              {filteredTickets.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhum chamado encontrado
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">ID</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Título</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Cliente/Empresa</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Responsável</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Prioridade</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Data</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTickets.map((ticket) => (
                      <tr key={ticket._id} className="border-b hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4">
                          <div className="text-sm font-semibold text-gray-700">
                            #{String(ticket.ticket_number).padStart(4, '0')}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <div className="font-medium text-gray-900">{ticket.title}</div>
                            <div className="text-sm text-gray-600">{ticket.description}</div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-700">{getClientName(ticket.client_id)}</td>
                        <td className="py-3 px-4 text-gray-700">{getUserName(ticket.responsible_id)}</td>
                        <td className="py-3 px-4">
                          <Badge className={getStatusColor(ticket.status)}>
                            {ticket.status === 'novo' ? 'Novo' : ticket.status === 'em_andamento' ? 'Em Andamento' : ticket.status === 'concluido' ? 'Concluído' : 'Cancelado'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={getPriorityColor(ticket.priority)}>
                            {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-gray-700">
                          {new Date(ticket.created_at).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewDetails(ticket)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Ver
                            </Button>
                            {isAdminOrTech && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteTicket(ticket._id, ticket.ticket_number)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Ticket Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Chamado</DialogTitle>
            <DialogDescription>Crie um novo chamado de suporte</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTicket} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Título *</Label>
              <Input
                id="title"
                value={newTicketData.title}
                onChange={(e) => setNewTicketData({ ...newTicketData, title: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição *</Label>
              <Textarea
                id="description"
                value={newTicketData.description}
                onChange={(e) => setNewTicketData({ ...newTicketData, description: e.target.value })}
                rows={4}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Prioridade</Label>
              <Select
                value={newTicketData.priority}
                onValueChange={(value) => setNewTicketData({ ...newTicketData, priority: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="baixa">Baixa</SelectItem>
                  <SelectItem value="media">Média</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(() => {
              const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
              return currentUser.role === 'admin' || currentUser.role === 'tecnico' ? (
                <div className="space-y-2">
                  <Label htmlFor="client_id">Cliente *</Label>
                  <Select
                    value={newTicketData.client_id}
                    onValueChange={(value) => setNewTicketData({ ...newTicketData, client_id: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client._id} value={client._id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null;
            })()}

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#4A2B7F] hover:bg-[#6B46C1]">
                Criar Chamado
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Relatório de Chamados</DialogTitle>
            <DialogDescription>Gere relatórios quantitativos por período</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Data Inicial</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={reportDates.start_date}
                  onChange={(e) => setReportDates({ ...reportDates, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">Data Final</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={reportDates.end_date}
                  onChange={(e) => setReportDates({ ...reportDates, end_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_filter">Cliente</Label>
                <Select
                  value={reportDates.client_id}
                  onValueChange={(value) => setReportDates({ ...reportDates, client_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os clientes</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client._id} value={client._id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button onClick={handleGenerateReport} className="w-full bg-[#4A2B7F] hover:bg-[#6B46C1]">
              Gerar Relatório
            </Button>

            {reportData && (
              <div className="space-y-4 mt-6">
                <Button 
                  onClick={() => {
                    const jsPDF = require('jspdf').jsPDF;
                    require('jspdf-autotable');
                    
                    const doc = new jsPDF();
                    
                    // Title
                    doc.setFontSize(18);
                    doc.text('Relatório de Chamados - NexTI', 14, 20);
                    
                    // Period
                    doc.setFontSize(10);
                    if (reportDates.start_date || reportDates.end_date) {
                      const period = `Período: ${reportDates.start_date || 'Início'} até ${reportDates.end_date || 'Hoje'}`;
                      doc.text(period, 14, 28);
                    }
                    
                    // Summary
                    doc.setFontSize(14);
                    doc.text('Resumo Geral', 14, 40);
                    doc.setFontSize(10);
                    doc.text(`Total: ${reportData.total}`, 14, 48);
                    doc.text(`Novos: ${reportData.byStatus.novo}`, 14, 54);
                    doc.text(`Em Andamento: ${reportData.byStatus.em_andamento}`, 14, 60);
                    doc.text(`Concluídos: ${reportData.byStatus.concluido}`, 14, 66);
                    doc.text(`Cancelados: ${reportData.byStatus.cancelado}`, 14, 72);
                    
                    // Table by client
                    const tableData = Object.entries(reportData.byClient).map(([client, stats]) => [
                      client,
                      stats.total,
                      stats.novo || 0,
                      stats.em_andamento || 0,
                      stats.concluido || 0
                    ]);
                    
                    doc.autoTable({
                      startY: 85,
                      head: [['Cliente', 'Total', 'Novos', 'Em Andamento', 'Concluídos']],
                      body: tableData,
                    });
                    
                    doc.save(`relatorio-chamados-${new Date().toISOString().split('T')[0]}.pdf`);
                  }}
                  variant="outline"
                  className="w-full"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Exportar PDF
                </Button>
              </div>
            )}
            
            {reportData && (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Resumo Geral</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-5 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold">{reportData.total}</p>
                        <p className="text-sm text-gray-600">Total</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-yellow-600">{reportData.byStatus.novo}</p>
                        <p className="text-sm text-gray-600">Novos</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-600">{reportData.byStatus.em_andamento}</p>
                        <p className="text-sm text-gray-600">Em Andamento</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-600">{reportData.byStatus.concluido}</p>
                        <p className="text-sm text-gray-600">Concluídos</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-red-600">{reportData.byStatus.cancelado}</p>
                        <p className="text-sm text-gray-600">Cancelados</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Por Cliente</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-4">Cliente</th>
                            <th className="text-center py-2 px-4">Total</th>
                            <th className="text-center py-2 px-4">Novos</th>
                            <th className="text-center py-2 px-4">Em Andamento</th>
                            <th className="text-center py-2 px-4">Concluídos</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(reportData.byClient).map(([client, stats]) => (
                            <tr key={client} className="border-b">
                              <td className="py-2 px-4">{client}</td>
                              <td className="text-center py-2 px-4 font-semibold">{stats.total}</td>
                              <td className="text-center py-2 px-4 text-yellow-600">{stats.novo || 0}</td>
                              <td className="text-center py-2 px-4 text-blue-600">{stats.em_andamento || 0}</td>
                              <td className="text-center py-2 px-4 text-green-600">{stats.concluido || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTicket?.title}</DialogTitle>
            <DialogDescription>
              Detalhes e interações do chamado
            </DialogDescription>
          </DialogHeader>
          
          {selectedTicket && (
            <div className="space-y-6">
              {/* Ticket Details */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Cliente</p>
                  <p className="font-semibold">{getClientName(selectedTicket.client_id)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Responsável</p>
                  <Select
                    value={selectedTicket.responsible_id || 'none'}
                    onValueChange={(value) => handleUpdateTicket('responsible_id', value === 'none' ? null : value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Não atribuído</SelectItem>
                      {users.filter(u => u.role === 'tecnico' || u.role === 'admin').map((user) => (
                        <SelectItem key={user._id} value={user._id}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Status</p>
                  <Select
                    value={selectedTicket.status}
                    onValueChange={(value) => handleUpdateTicket('status', value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="novo">Novo</SelectItem>
                      <SelectItem value="em_andamento">Em Andamento</SelectItem>
                      <SelectItem value="concluido">Concluído</SelectItem>
                      <SelectItem value="cancelado">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Prioridade</p>
                  <Badge className={getPriorityColor(selectedTicket.priority)}>
                    {selectedTicket.priority.charAt(0).toUpperCase() + selectedTicket.priority.slice(1)}
                  </Badge>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-600">Descrição</p>
                  <p className="mt-1">{selectedTicket.description}</p>
                </div>
              </div>

              {/* Interactions */}
              <div>
                <h3 className="font-semibold mb-3">Interações</h3>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {interactions.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">Nenhuma interação ainda</p>
                  ) : (
                    interactions.map((interaction) => (
                      <div key={interaction._id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-start mb-1">
                          <p className="font-medium text-sm">{getUserName(interaction.user_id)}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(interaction.created_at).toLocaleString('pt-BR')}
                          </p>
                        </div>
                        <p className="text-sm text-gray-700">{interaction.content}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Add Interaction */}
              <div className="space-y-2">
                <h3 className="font-semibold">Adicionar Comentário</h3>
                <Textarea
                  placeholder="Digite seu comentário..."
                  value={newInteraction}
                  onChange={(e) => setNewInteraction(e.target.value)}
                  rows={3}
                />
                <Button
                  onClick={handleAddInteraction}
                  className="bg-[#4A2B7F] hover:bg-[#6B46C1]"
                  disabled={!newInteraction.trim()}
                >
                  Adicionar Comentário
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Relatórios com Filtros */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gerar Relatório de Chamados</DialogTitle>
            <DialogDescription>
              Configure os filtros para gerar um relatório personalizado em PDF
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Filtro de Período */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Início</Label>
                <Input
                  type="date"
                  value={reportFilters.start_date ? reportFilters.start_date.split('T')[0] : ''}
                  onChange={(e) => {
                    const filters = { 
                      ...reportFilters, 
                      start_date: e.target.value ? new Date(e.target.value).toISOString() : null 
                    };
                    setReportFilters(filters);
                    updateReportPreview(filters);
                  }}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Data Fim</Label>
                <Input
                  type="date"
                  value={reportFilters.end_date ? reportFilters.end_date.split('T')[0] : ''}
                  onChange={(e) => {
                    const filters = { 
                      ...reportFilters, 
                      end_date: e.target.value ? new Date(e.target.value).toISOString() : null 
                    };
                    setReportFilters(filters);
                    updateReportPreview(filters);
                  }}
                  className="w-full"
                />
              </div>
            </div>
            
            {/* Filtro de Status (Múltipla Seleção) */}
            <div className="space-y-2">
              <Label>Status</Label>
              <div className="flex flex-wrap gap-2">
                {['novo', 'em_andamento', 'concluido', 'cancelado'].map((status) => {
                  const isSelected = reportFilters.status.includes(status);
                  const labels = {
                    'novo': 'Novo',
                    'em_andamento': 'Em Andamento',
                    'concluido': 'Concluído',
                    'cancelado': 'Cancelado'
                  };
                  
                  return (
                    <Button
                      key={status}
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        const newStatus = isSelected
                          ? reportFilters.status.filter(s => s !== status)
                          : [...reportFilters.status, status];
                        const filters = { ...reportFilters, status: newStatus };
                        setReportFilters(filters);
                        updateReportPreview(filters);
                      }}
                      className={isSelected ? "bg-[#4A2B7F] hover:bg-[#6B46C1]" : ""}
                    >
                      {labels[status]}
                    </Button>
                  );
                })}
              </div>
            </div>
            
            {/* Filtro de Cliente */}
            <div className="space-y-2">
              <Label>Cliente</Label>
              <select
                className="w-full p-2 border rounded-md"
                value={reportFilters.client_id || ''}
                onChange={(e) => {
                  const filters = { ...reportFilters, client_id: e.target.value || null };
                  setReportFilters(filters);
                  updateReportPreview(filters);
                }}
              >
                <option value="">Todos os clientes</option>
                {clients.map((client) => (
                  <option key={client._id} value={client._id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Filtro de Responsável */}
            <div className="space-y-2">
              <Label>Responsável</Label>
              <select
                className="w-full p-2 border rounded-md"
                value={reportFilters.responsible_id || ''}
                onChange={(e) => {
                  const filters = { ...reportFilters, responsible_id: e.target.value || null };
                  setReportFilters(filters);
                  updateReportPreview(filters);
                }}
              >
                <option value="">Todos os responsáveis</option>
                {users.filter(u => u.role === 'tecnico' || u.role === 'admin').map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Filtro de Criado Por */}
            <div className="space-y-2">
              <Label>Criado Por</Label>
              <select
                className="w-full p-2 border rounded-md"
                value={reportFilters.created_by || ''}
                onChange={(e) => {
                  const filters = { ...reportFilters, created_by: e.target.value || null };
                  setReportFilters(filters);
                  updateReportPreview(filters);
                }}
              >
                <option value="">Todos os usuários</option>
                {users.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Pré-visualização */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">Pré-visualização</p>
                  {reportPreview.loading ? (
                    <p className="text-sm text-blue-700">Carregando...</p>
                  ) : (
                    <p className="text-sm text-blue-700">
                      {reportPreview.count} {reportPreview.count === 1 ? 'chamado será incluído' : 'chamados serão incluídos'} no relatório
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowReportModal(false);
                setReportFilters({
                  start_date: null,
                  end_date: null,
                  status: [],
                  client_id: null,
                  responsible_id: null,
                  created_by: null
                });
              }}
              disabled={generatingReport}
            >
              Cancelar
            </Button>
            
            <Button
              onClick={handleGenerateReportPDF}
              disabled={generatingReport || reportPreview.count === 0}
              className="gap-2 bg-[#4A2B7F] hover:bg-[#6B46C1]"
            >
              {generatingReport ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Gerando PDF...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4" />
                  Gerar Relatório
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Chamados;